/**
 * Helper functions for checking IELTS test scores and AI scoring
 */

/**
 * Parse analytics from string or object
 * @param {Object|string} analytics - Analytics object or JSON string
 * @returns {Object} Parsed analytics object
 */
export const parseAnalytics = (analytics) => {
  if (!analytics) return {};
  if (typeof analytics === 'string') {
    try {
      return JSON.parse(analytics);
    } catch (e) {
      return {};
    }
  }
  return analytics || {};
};

/**
 * Check if AI scoring exists (even if confidence is low or band is 0)
 * The AI backend returns a response structure even when it "fails" (e.g., insufficient word count),
 * so we check for the presence of overall_band in ai_scoring rather than just status
 * @param {Object} apiResult - API result object with response
 * @returns {boolean} True if AI scoring exists (regardless of confidence or band score)
 */
export const hasAIScore = (apiResult) => {
  if (!apiResult?.response) return false;
  const analytics = parseAnalytics(apiResult.response.analytics);
  // Check if ai_scoring exists and has overall_band defined (even if 0)
  // This handles cases where AI returns results even on "failure" (low confidence, insufficient content, etc.)
  return !!(analytics?.ai_scoring && analytics.ai_scoring.overall_band !== undefined && analytics.ai_scoring.overall_band !== null);
};

/**
 * Check if a score is available (handles 0 correctly)
 * @param {Object} apiResult - API result object with response
 * @returns {boolean} True if score is available (including 0)
 */
export const hasScore = (apiResult) => {
  if (!apiResult?.response) return false;
  const totalMarksObtained = apiResult.response.totalMarksObtained;
  const status = apiResult.response.status;
  
  // Check if totalMarksObtained exists (even if 0)
  const hasTotalMarks = totalMarksObtained !== null && 
                        totalMarksObtained !== undefined && 
                        totalMarksObtained !== '';
  
  // Also check for AI scoring
  const hasAI = hasAIScore(apiResult);
  
  return (hasTotalMarks || hasAI) && status !== 'pending_evaluation';
};

/**
 * Get the band score (from AI scoring or totalMarksObtained)
 * @param {Object} apiResult - API result object with response
 * @returns {number|null} Band score or null if not available
 */
export const getBandScore = (apiResult) => {
  if (!apiResult?.response) return null;
  
  const analytics = parseAnalytics(apiResult.response.analytics);
  const aiScoring = analytics?.ai_scoring;
  const totalMarksObtained = apiResult.response.totalMarksObtained;
  
  // Prefer AI scoring band if available
  if (aiScoring?.overall_band !== undefined && aiScoring.overall_band !== null) {
    return aiScoring.overall_band;
  }
  
  // Fallback to totalMarksObtained (convert string to number if needed)
  if (totalMarksObtained !== null && totalMarksObtained !== undefined && totalMarksObtained !== '') {
    return typeof totalMarksObtained === 'string' ? parseFloat(totalMarksObtained) : totalMarksObtained;
  }
  
  return null;
};

/**
 * Check if test status is pending evaluation
 * @param {Object} apiResult - API result object with response
 * @returns {boolean} True if status is pending_evaluation
 */
export const isPending = (apiResult) => {
  if (!apiResult?.response) return false;
  return apiResult.response.status === 'pending_evaluation';
};

/**
 * Check if test is completed
 * @param {Object} apiResult - API result object with response
 * @returns {boolean} True if status is completed
 */
export const isCompleted = (apiResult) => {
  if (!apiResult?.response) return false;
  return apiResult.response.status === 'completed';
};

/**
 * Get AI scoring object from apiResult
 * @param {Object} apiResult - API result object with response
 * @returns {Object|null} AI scoring object or null
 */
export const getAIScoring = (apiResult) => {
  if (!apiResult?.response) return null;
  const analytics = parseAnalytics(apiResult.response.analytics);
  return analytics?.ai_scoring || null;
};

/**
 * Check if score should be displayed (for Writing/Speaking with AI scoring or any score)
 * @param {Object} apiResult - API result object with response
 * @returns {boolean} True if score should be displayed
 */
export const shouldShowScore = (apiResult) => {
  if (!apiResult?.response) return false;
  
  // Check for AI scoring (even if band is 0)
  const hasAI = hasAIScore(apiResult);
  
  // Check for totalMarksObtained (handles 0 correctly)
  const totalMarksObtained = apiResult.response.totalMarksObtained;
  const hasTotalMarks = totalMarksObtained !== null && 
                        totalMarksObtained !== undefined && 
                        totalMarksObtained !== '';
  
  const status = apiResult.response.status;
  
  return (hasAI || hasTotalMarks) && status !== 'pending_evaluation';
};

